import { useMemo } from "react";
import { PieChart, Pie, Cell, Tooltip as RechartsTooltip, ResponsiveContainer } from "recharts";
import { Box, Typography } from "@mui/material";

// marker ids — fatty acid family percentages, must sum to 100%
const MARKER_IDS = { omega3: 30, omega6: 40, mufa: 60, sfa: 55 };

export default function OmegaCompositionChart({ results, staticValues, theme, t }) {
  const slices = useMemo(() => {
    let values;
    if (staticValues) {
      values = staticValues;
    } else {
      const find = (id) => (results || []).find((r) => Number(r.marker) === id);
      const raw = {
        omega3: find(MARKER_IDS.omega3),
        omega6: find(MARKER_IDS.omega6),
        mufa:   find(MARKER_IDS.mufa),
        sfa:    find(MARKER_IDS.sfa),
      };
      const hasAll = Object.values(raw).every((m) => m && m.value != null);
      if (!hasAll) return null;
      values = Object.fromEntries(Object.entries(raw).map(([k, m]) => [k, Number(m.value)]));
    }

    const sum = Object.values(values).reduce((a, b) => a + b, 0);
    if (!sum) return null;

    const COLORS = {
      omega3: theme.palette.tertiary.C300,
      omega6: theme.palette.quartiary.C300,
      mufa:   theme.palette.tertiary.C600,
      sfa:    theme.palette.quartiary.C600,
    };
    const LABELS = {
      omega3: t("bt_omega_chart_omega3"),
      omega6: t("bt_omega_chart_omega6"),
      mufa:   t("bt_omega_chart_mufa"),
      sfa:    t("bt_omega_chart_sfa"),
    };

    // rescale to an exact 100% sum to absorb backend rounding drift
    return Object.keys(values).map((key) => ({
      key,
      name:  LABELS[key],
      value: (values[key] / sum) * 100,
      color: COLORS[key],
    }));
  }, [results, staticValues, theme, t]);

  if (!slices) {
    return (
      <Box sx={{ textAlign: "center", py: 4 }}>
        <Typography color="text.secondary">{t("bt_omega_chart_no_data")}</Typography>
      </Box>
    );
  }

  const RADIAN = Math.PI / 180;

  // leader-line label: slice name + percentage, anchored outside the donut, colored per slice
  const renderAnnotation = ({ cx, cy, midAngle, outerRadius, value, name, color }) => {
    const sin = Math.sin(-RADIAN * midAngle);
    const cos = Math.cos(-RADIAN * midAngle);
    const sx  = cx + (outerRadius + 6)  * cos;
    const sy  = cy + (outerRadius + 6)  * sin;
    const mx  = cx + (outerRadius + 22) * cos;
    const my  = cy + (outerRadius + 22) * sin;
    const ex  = mx + (cos >= 0 ? 1 : -1) * 12;
    const ey  = my;
    const textAnchor = cos >= 0 ? "start" : "end";
    const tx = ex + (cos >= 0 ? 6 : -6);

    return (
      <g key={name}>
        <path d={`M${sx},${sy}L${mx},${my}L${ex},${ey}`} stroke={color} fill="none" strokeWidth={1.5} />
        <circle cx={ex} cy={ey} r={2.5} fill={color} stroke="none" />
        <text x={tx} y={ey - 4} textAnchor={textAnchor} fontSize={12} fontWeight={700} fill={color}>
          {name}
        </text>
        <text x={tx} y={ey + 12} textAnchor={textAnchor} fontSize={12} fontWeight={600} fill={theme.palette.text.secondary}>
          {`${value.toFixed(1)}%`}
        </text>
      </g>
    );
  };

  return (
    <Box sx={{ width: "100%" }}>
      <Box sx={{ position: "relative", width: "100%", height: 320 }}>
        <ResponsiveContainer width="100%" height="100%">
          <PieChart margin={{ top: 24, right: 76, bottom: 24, left: 36 }}>
            <Pie
              data={slices}
              dataKey="value"
              nameKey="name"
              innerRadius={62}
              outerRadius={92}
              paddingAngle={2}
              startAngle={90}
              endAngle={-270}
              label={renderAnnotation}
              labelLine={false}
              isAnimationActive
            >
              {slices.map((s) => (
                <Cell key={s.key} fill={s.color} stroke={theme.palette.background.paper} strokeWidth={2} />
              ))}
            </Pie>
            <RechartsTooltip
              contentStyle={{
                borderRadius: 10, border: `1px solid ${theme.palette.divider}`,
                backgroundColor: theme.palette.background.paper,
                boxShadow: "0 4px 16px rgba(0,0,0,0.12)", fontSize: 13,
              }}
              formatter={(value, name) => [`${Number(value).toFixed(1)}%`, name]}
            />
          </PieChart>
        </ResponsiveContainer>
        <Box sx={{
          position: "absolute", top: "50%", left: "calc(50% - 20px)", transform: "translate(-50%, -50%)",
          textAlign: "center", pointerEvents: "none",
        }}>
          <Typography variant="h5" fontWeight={800}>100%</Typography>
          <Typography variant="caption" color="text.secondary" sx={{ textTransform: "uppercase", letterSpacing: 0.5 }}>
            {t("bt_omega_chart_total_label")}
          </Typography>
        </Box>
      </Box>
    </Box>
  );
}
